import React, { useState } from 'react';
import { X, DollarSign, Calendar, Percent, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { LoanApproval } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface LoanApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  loanApplication: LoanApproval | null;
  onApprove?: (
    applicationId: string,
    approvalData: {
      approvedAmount: number;
      interestRate: number;
      term: number;
      notes?: string;
    }
  ) => Promise<boolean>;
  onReject?: (applicationId: string, reason: string) => Promise<boolean>;
  // When true, modal is read-only and approval/rejection actions are hidden
  readOnly?: boolean;
}

const LoanApprovalModal: React.FC<LoanApprovalModalProps> = ({
  isOpen,
  onClose,
  loanApplication,
  onApprove,
  onReject,
  readOnly = false,
}) => {
  const [showApprovalForm, setShowApprovalForm] = useState(true);
  const [approvedAmount, setApprovedAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [term, setTerm] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  React.useEffect(() => {
    if (loanApplication) {
      setApprovedAmount(loanApplication.requestedAmount.toString());
      setTerm(loanApplication.term.toString());
      setInterestRate('10'); // Default interest rate
    }
  }, [loanApplication]);

  if (!isOpen || !loanApplication) return null;

  const calculateMonthlyPayment = () => {
    const principal = parseFloat(approvedAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 100 / 12;
    const months = parseInt(term) || 1;

    if (rate === 0) {
      return principal / months;
    }

    const monthlyPayment = principal * (rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    return monthlyPayment;
  };

  const calculateTotalRepayment = () => {
    return calculateMonthlyPayment() * (parseInt(term) || 1);
  };

  const handleApprove = async () => {
    if (loading) return;

    const amount = parseFloat(approvedAmount);
    const rate = parseFloat(interestRate);
    const months = parseInt(term);

    if (!amount || !rate || !months || amount <= 0 || rate < 0 || months <= 0) {
      toast.error('Please provide valid approval details');
      return;
    }

    setLoading(true);
    toast.info('Approving loan application...');
    
    const approvalData: any = {
      approvedAmount: amount,
      interestRate: rate,
      term: months,
    };
    if (notes.trim()) {
      approvalData.notes = notes.trim();
    }
    if (!onApprove) {
      toast.error('Approval is not available');
      setLoading(false);
      return;
    }

    const success = await onApprove(loanApplication.id, approvalData);
    setLoading(false);

    if (success) {
      toast.success(`Loan approved successfully for ${formatCurrency(amount)}!`);
      resetForm();
      onClose();
    } else {
      toast.error('Failed to approve loan. Please try again.');
    }
  };

  const handleReject = async () => {
    if (loading || !rejectionReason.trim()) return;

    setLoading(true);
    toast.info('Processing loan rejection...');
    
    if (!onReject) {
      toast.error('Rejection is not available');
      setLoading(false);
      return;
    }

    const success = await onReject(loanApplication.id, rejectionReason);
    setLoading(false);

    if (success) {
      toast.success('Loan application rejected successfully');
      resetForm();
      onClose();
    } else {
      toast.error('Failed to reject loan. Please try again.');
    }
  };

  const resetForm = () => {
    setApprovedAmount('');
    setInterestRate('');
    setTerm('');
    setNotes('');
    setRejectionReason('');
    setShowApprovalForm(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Loan Application Review</h2>
            <p className="text-sm text-gray-600">Review and approve or reject loan application</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Application Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Application Details</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Requested Amount</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(loanApplication.requestedAmount)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Requested Term</p>
                    <p className="text-sm font-medium text-gray-900">{loanApplication.term} months</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Purpose</p>
                    <p className="text-sm font-medium text-gray-900">{loanApplication.purpose}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Loan Type</p>
                    <p className="text-sm font-medium text-gray-900">{loanApplication.loanType}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Submitted Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(loanApplication.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      loanApplication.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      loanApplication.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      loanApplication.status === 'UNDER_REVIEW' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {loanApplication.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {showApprovalForm ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Approval Details</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approved Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                      ₦
                    </span>
                    <input
                      type="number"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interest Rate (% per annum) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="10.0"
                      step="0.1"
                      min="0"
                      max="30"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                      %
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Term (months) *
                  </label>
                  <input
                    type="number"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="12"
                    min="1"
                    max="360"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Add any notes or conditions..."
                  />
                </div>

                {/* Loan Summary */}
                {approvedAmount && interestRate && term && (
                  <div className="mt-4 p-4 bg-primary-50 rounded-lg space-y-2">
                    <h4 className="text-sm font-medium text-gray-900">Loan Summary</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Monthly Payment:</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(calculateMonthlyPayment())}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Repayment:</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(calculateTotalRepayment())}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Interest:</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(calculateTotalRepayment() - parseFloat(approvedAmount))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Rejection Details</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Please provide a detailed reason for rejection..."
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Close
          </button>

          {!readOnly && (
            <>
              {showApprovalForm ? (
                <>
                  <button
                    onClick={() => setShowApprovalForm(false)}
                    className="btn-danger"
                    disabled={loading}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    className="btn-success"
                    disabled={loading || !approvedAmount || !interestRate || !term}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve Loan
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowApprovalForm(true);
                      setRejectionReason('');
                    }}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Back to Approval
                  </button>
                  <button
                    onClick={handleReject}
                    className="btn-danger"
                    disabled={loading || !rejectionReason.trim()}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 mr-2" />
                        Confirm Rejection
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoanApprovalModal;

